import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ROLE_LABELS } from "../lib/constants";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { MapPin, Settings, ChevronDown, Gauge, KeyRound, LogOut } from "lucide-react";
import { cn } from "../lib/utils";
import ChangePasswordDialog from "./ChangePasswordDialog";

export default function TopBar() {
  const { activeUser, logout, locations, activeLocationIds, setLocationIds } = useApp();
  const navigate = useNavigate();
  const loc = useLocation();
  const [cpOpen, setCpOpen] = useState(false);

  const visibleLocs = locations.filter((l) =>
    !activeUser?.location_ids?.length || activeUser.location_ids.includes(l.id),
  );

  // Cross-section of selected vs. visible — we never show a tick for a
  // location the user no longer has access to.
  const selectedVisible = visibleLocs.filter((l) => activeLocationIds.includes(l.id));
  const allSelected = visibleLocs.length > 0 && selectedVisible.length === visibleLocs.length;

  // Header label rules (per spec):
  //   • All visible selected → "All Locations"
  //   • 1 selected           → "Jeypore"
  //   • 2 selected           → "Jeypore, Kotpad"
  //   • 3+                   → "Jeypore +N"
  //   • 0                    → "Select Locations"
  let headerLabel;
  if (visibleLocs.length === 0) {
    headerLabel = "No locations";
  } else if (allSelected) {
    headerLabel = "All Locations";
  } else if (selectedVisible.length === 0) {
    headerLabel = "Select Locations";
  } else if (selectedVisible.length === 1) {
    headerLabel = selectedVisible[0].name;
  } else if (selectedVisible.length === 2) {
    headerLabel = `${selectedVisible[0].name}, ${selectedVisible[1].name}`;
  } else {
    headerLabel = `${selectedVisible[0].name} +${selectedVisible.length - 1}`;
  }

  const toggleLoc = (id) => {
    if (activeLocationIds.includes(id)) {
      setLocationIds(activeLocationIds.filter((x) => x !== id));
    } else {
      setLocationIds([...activeLocationIds, id]);
    }
  };
  const selectAllLocs = () => setLocationIds(visibleLocs.map((l) => l.id));
  const clearAllLocs = () => setLocationIds([]);

  const navLink = (to, label) => (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={cn(
        "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
        loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to))
          ? "bg-primary text-primary-foreground"
          : "text-slate-700 hover:bg-slate-100",
      )}
    >
      {label}
    </Link>
  );

  const initials = (activeUser?.name || "?")
    .split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-border bg-white/80 backdrop-blur-xl"
      data-testid="top-bar"
    >
      <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Gauge className="h-5 w-5" strokeWidth={2.25} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-base font-semibold tracking-tight text-slate-900">
              OneDesk <span className="text-primary">360</span>
            </div>
            <div className="hidden text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500 sm:block">
              IVA Motors · Yamaha
            </div>
          </div>
        </Link>

        <nav className="ml-2 hidden items-center gap-1 md:flex">
          {navLink("/", "Dashboard")}
          {navLink("/sales", "Sales")}
          {(activeUser?.role === "PDI" || activeUser?.role === "SalesManager" || activeUser?.role === "Admin") && navLink("/pdi", "PDI")}
          {(activeUser?.role === "Accounts" || activeUser?.role === "SalesManager" || activeUser?.role === "Admin") && navLink("/accounts", "Accounts")}
          {(activeUser?.role === "BackOffice" || activeUser?.role === "SalesManager" || activeUser?.role === "Admin") && navLink("/back-office", "Back Office")}
          {(activeUser?.role === "Spares" || activeUser?.role === "SalesManager" || activeUser?.role === "Admin") && navLink("/spares", "Spares")}
          {(activeUser?.role === "DSE" || activeUser?.role === "SalesManager" ||
            activeUser?.role === "Accounts" ||
            activeUser?.role === "BackOffice" || activeUser?.role === "Admin") &&
            navLink("/delivery-files", "Delivery Files")}
          {navLink("/tasks", "Tasks")}
          {(activeUser?.role === "Admin" || activeUser?.role === "SalesManager") && navLink("/reports", "Reports")}
          {(activeUser?.role === "Admin" || activeUser?.role === "SalesManager") && navLink("/admin", activeUser?.role === "Admin" ? "Admin" : "Manage")}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {/* Location filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-9 max-w-[220px] gap-1.5 truncate border-slate-200 bg-white"
                data-testid="location-selector"
              >
                <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                <span
                  className="hidden truncate text-xs sm:inline"
                  data-testid="location-selector-label"
                  title={headerLabel}
                >
                  {headerLabel}
                </span>
                <span className="text-xs sm:hidden">
                  {selectedVisible.length}/{visibleLocs.length}
                </span>
                <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel>Locations</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={allSelected}
                onCheckedChange={(checked) => (checked ? selectAllLocs() : clearAllLocs())}
                onSelect={(e) => e.preventDefault()}
                data-testid="location-select-all"
                className="font-semibold"
              >
                Select All
              </DropdownMenuCheckboxItem>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); clearAllLocs(); }}
                disabled={selectedVisible.length === 0}
                data-testid="location-clear-all"
                className="pl-8 text-xs text-slate-600"
              >
                Clear All
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {visibleLocs.map((l) => (
                <DropdownMenuCheckboxItem
                  key={l.id}
                  checked={activeLocationIds.includes(l.id)}
                  onCheckedChange={() => toggleLoc(l.id)}
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`location-option-${l.code}`}
                >
                  <span className="font-mono-data text-xs text-slate-500 mr-2">{l.code}</span>
                  {l.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User menu (replaces former Role Switcher) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2 border-slate-200 bg-white pr-2.5"
                data-testid="user-menu-trigger"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                  {initials}
                </span>
                <span className="hidden flex-col items-start leading-tight sm:flex">
                  <span className="text-xs font-medium text-slate-900">{activeUser?.name}</span>
                  <span className="text-[9px] uppercase tracking-wider text-slate-500">
                    {ROLE_LABELS[activeUser?.role] || activeUser?.role}
                  </span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex flex-col gap-0.5">
                <span className="text-sm">{activeUser?.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500">
                  {ROLE_LABELS[activeUser?.role] || activeUser?.role}
                  {activeUser?.mobile ? ` · ${activeUser.mobile}` : ""}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setCpOpen(true)}
                data-testid="menu-change-password"
              >
                <KeyRound className="mr-2 h-4 w-4" /> Change Password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="menu-logout"
                className="text-rose-600 focus:text-rose-700"
              >
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {(activeUser?.role === "Admin" || activeUser?.role === "SalesManager") && (
            <Button
              variant="ghost" size="icon" className="h-9 w-9"
              onClick={() => navigate("/admin")}
              data-testid="admin-settings-btn"
              title={activeUser?.role === "SalesManager" ? "Manage users, masters" : "Admin"}
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Mobile nav */}
      <div className="flex items-center gap-1 overflow-x-auto border-t border-border px-3 py-2 md:hidden">
        {navLink("/", "Dashboard")}
        {navLink("/sales", "Sales")}
        {(activeUser?.role === "PDI" || activeUser?.role === "SalesManager" || activeUser?.role === "Admin") && navLink("/pdi", "PDI")}
        {(activeUser?.role === "Accounts" || activeUser?.role === "SalesManager" || activeUser?.role === "Admin") && navLink("/accounts", "Accounts")}
        {(activeUser?.role === "BackOffice" || activeUser?.role === "SalesManager" || activeUser?.role === "Admin") && navLink("/back-office", "Back Office")}
        {(activeUser?.role === "Spares" || activeUser?.role === "SalesManager" || activeUser?.role === "Admin") && navLink("/spares", "Spares")}
        {(activeUser?.role === "DSE" || activeUser?.role === "SalesManager" ||
          activeUser?.role === "Accounts" ||
          activeUser?.role === "BackOffice" || activeUser?.role === "Admin") &&
          navLink("/delivery-files", "Delivery Files")}
        {navLink("/tasks", "Tasks")}
        {(activeUser?.role === "Admin" || activeUser?.role === "SalesManager") && navLink("/reports", "Reports")}
        {(activeUser?.role === "Admin" || activeUser?.role === "SalesManager") && navLink("/admin", activeUser?.role === "Admin" ? "Admin" : "Manage")}
      </div>

      <ChangePasswordDialog open={cpOpen} onClose={() => setCpOpen(false)} />
    </header>
  );
}
